import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const STORAGE_KEY = 'varotrade_app_banner_dismissed_v2';

/**
 * MobileAppBanner — INVERTED style, appears AFTER scroll, slides up from bottom-above-nav.
 *  • Dominant flame-orange background (brand-forward)
 *  • White pill with black "تثبيت" text as CTA
 *  • VAROTRADES emblem in white square
 *  • Dismissible (localStorage)
 *  • Only appears after 300px scroll → preserves the header + video immediacy at top
 */
const MobileAppBanner = () => {
  const [dismissed, setDismissed] = useState(true);
  const [visible, setVisible] = useState(false);
  const { isRTL } = useLanguage();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const d = !!localStorage.getItem(STORAGE_KEY);
    setDismissed(d);
    if (d) return;
    const onScroll = () => setVisible(window.scrollY > 320);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dismiss = (e) => {
    e?.preventDefault(); e?.stopPropagation();
    setVisible(false);
    setDismissed(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
  };

  if (dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.a
          href="/app"
          initial={{ y: 140, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 140, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="lg:hidden fixed inset-x-3 z-[58] rounded-2xl bg-flame-500 text-white overflow-hidden"
          style={{ bottom: 'calc(72px + env(safe-area-inset-bottom))' }}
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="mobile-app-banner"
        >
          {/* Subtle glow accents — keeps it brand-forward */}
          <div className="absolute -top-10 -end-10 w-36 h-36 rounded-full bg-flame-300/40 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -start-10 w-32 h-32 rounded-full bg-flame-700/30 blur-2xl pointer-events-none" />

          <div className="relative flex items-center gap-3 px-3 py-3">
            <button
              onClick={dismiss}
              aria-label="Close"
              className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition-colors"
              data-testid="mobile-app-banner-close"
            >
              <X className="w-4 h-4" strokeWidth={2.4} />
            </button>

            {/* White tile with flame icon */}
            <div className="shrink-0 w-11 h-11 rounded-xl bg-white flex items-center justify-center">
              <Zap className="w-6 h-6 text-flame-600 fill-flame-600" strokeWidth={2.2} />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-black leading-tight tracking-tight">
                {isRTL ? 'تطبيق VAROTRADES' : 'VAROTRADES App'}
              </p>
              <p className="text-[11.5px] text-white/90 leading-tight mt-0.5 truncate">
                {isRTL ? 'ثبّته الآن · بدون متجر' : 'Install now · no store needed'}
              </p>
            </div>

            {/* White pill with BLACK text for maximum inverted contrast */}
            <span
              className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white text-ink-900 text-[13px] font-black tracking-tight"
              data-testid="mobile-app-banner-install"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.6} />
              {isRTL ? 'تثبيت' : 'INSTALL'}
            </span>
          </div>
        </motion.a>
      )}
    </AnimatePresence>
  );
};

export default MobileAppBanner;
